'use client';

/**
 * Widget agent grant — the HOST-side dialog behind `host.agents.request()`.
 *
 * Security model mirrors {@link WidgetContentPicker}: portal chrome rendered
 * OVER the canvas, never inside the widget iframe. The widget never sees the
 * viewer's agent roster — only the grant refs the user confirms here, which
 * the bridge persists to `state.connectedResources` (the only writer besides
 * server-side install).
 *
 * Opened via a window CustomEvent('widget-agent-grant') whose detail carries
 * { reason?, multi?, widgetTitle, resolve } — the bridge awaits
 * `resolve(selection)`. Cancel / backdrop resolves null.
 *
 * Breadths: **All agents** (default) and **My agents** persist ONE wildcard
 * ref ({kind:'agent', id:'*', scope:['all'|'mine']}) that `resource.list`
 * expands live; **Specific agents** persists one ref per checked agent.
 */

import { useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { useAppFrameFocusReturn } from '@/components/workspace/app-frame-focus-return';
import { cn } from '@/lib/utils';
import type { ResourceRef } from '@/lib/workspaces/widget-permissions';

export type AgentGrantBreadth = 'all' | 'mine' | 'specific' | 'find';

export interface AgentGrantSelection {
  breadth: AgentGrantBreadth;
  refs: ResourceRef[];
}

interface GrantRequest {
  reason?: string;
  multi?: boolean;
  /** Which tab to open on. An app bringing somebody new in asks for `find`. */
  start?: AgentGrantBreadth;
  widgetTitle: string;
  resolve: (sel: AgentGrantSelection | null) => void;
}

interface AgentRow {
  id: string;
  name: string;
  handle?: string;
  /** Public search rows carry what a person weighs before letting one in. */
  owner?: string;
  trust?: string;
  description?: string;
}

const BREADTHS: Array<{ id: AgentGrantBreadth; label: string; hint: string }> = [
  { id: 'all', label: 'All agents', hint: 'Every agent this account can use, including ones added later' },
  { id: 'mine', label: 'My agents', hint: 'Agents you own, including ones you create later' },
  { id: 'specific', label: 'Specific agents', hint: 'Only the agents you check below' },
  { id: 'find', label: 'Find an agent', hint: 'Search the public agents on Robutler and let one in' },
];

/** The trust word a public row shows: the reputation tier the discovery index
 *  hands back (platinum, gold, silver, bronze, or a word of its own), shown as
 *  it is spelled with a capital. Anything empty stays unlabelled. */
const TRUST_LABEL: Record<string, string> = {
  verified: 'Verified',
  trusted: 'Trusted',
  established: 'Established',
  new: 'New here',
};
function trustWord(t: string | undefined): string | undefined {
  if (!t) return undefined;
  if (TRUST_LABEL[t]) return TRUST_LABEL[t];
  const clean = t.replace(/[_-]+/g, ' ').trim();
  return clean && clean.length <= 16 ? clean.charAt(0).toUpperCase() + clean.slice(1) : undefined;
}

function readTrust(a: Record<string, unknown>): string | undefined {
  for (const k of ['trustLevel', 'trust', 'reputationTier', 'tier']) {
    const v = a[k];
    if (typeof v === 'string' && v) return v.toLowerCase();
  }
  return undefined;
}

function readOwner(a: Record<string, unknown>): string | undefined {
  const o = a.owner;
  if (o && typeof o === 'object' && typeof (o as { username?: unknown }).username === 'string') {
    return (o as { username: string }).username;
  }
  for (const k of ['ownerUsername', 'ownerHandle']) {
    const v = a[k];
    if (typeof v === 'string' && v) return v;
  }
  return undefined;
}

export function WidgetAgentGrant() {
  const t = useT();
  const [req, setReq] = useState<GrantRequest | null>(null);
  // Close hands focus back to the app that asked (app-frame-focus-return.ts).
  const captureAppFocus = useAppFrameFocusReturn(!!req);
  const [breadth, setBreadth] = useState<AgentGrantBreadth>('all');
  const [roster, setRoster] = useState<AgentRow[] | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  // The public search: what was typed, what came back, and which rows are
  // ticked. Kept apart from the account roster so a tick in one tab never
  // leaks into the other.
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<AgentRow[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<Map<string, AgentRow>>(new Map());

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<GrantRequest>).detail;
      if (!detail || typeof detail.resolve !== 'function') return;
      captureAppFocus();
      setBreadth(detail.start || 'all'); // the default breadth, unless the app asked for a tab
      setRoster(null);
      setChecked(new Set());
      setQuery('');
      setFound(null);
      setSearching(false);
      setPicked(new Map());
      setReq(detail);
    };
    window.addEventListener('widget-agent-grant', onOpen);
    return () => window.removeEventListener('widget-agent-grant', onOpen);
  }, [captureAppFocus]);

  // Roster loads lazily, host-side, only while the dialog is up — the widget
  // never receives it unless the user confirms specific picks.
  useEffect(() => {
    if (!req || roster !== null) return;
    let dead = false;
    (async () => {
      try {
        const r = await fetch('/api/agents', { credentials: 'same-origin' });
        const data = (await r.json()) as { agents?: Array<Record<string, unknown>> };
        if (dead) return;
        const pickName = (a: Record<string, unknown>): string => {
          for (const k of ['displayName', 'name', 'username', 'localName', 'handle']) {
            const v = a[k];
            if (typeof v === 'string' && v) return v;
          }
          return `Agent ${String(a.id).slice(0, 6)}`;
        };
        setRoster(
          (data?.agents ?? [])
            .filter((a) => a?.id !== undefined && a.id !== null)
            .map((a) => ({ id: String(a.id), name: pickName(a) })),
        );
      } catch {
        if (!dead) setRoster([]);
      }
    })();
    return () => {
      dead = true;
    };
  }, [req, roster]);

  // The public search runs host-side against the discovery index, debounced,
  // and only while the Find tab is up. Rows carry the handle, the owner and the
  // trust word so the person weighs who they are letting in before ticking.
  useEffect(() => {
    if (!req || breadth !== 'find') return;
    const q = query.trim();
    if (q.length < 2) {
      setFound(null);
      setSearching(false);
      return;
    }
    let dead = false;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const r = await fetch(
          `/api/discovery/agents?search=${encodeURIComponent(q)}&type=agent&limit=12`,
          { credentials: 'same-origin' },
        );
        const data = (await r.json()) as { agents?: Array<Record<string, unknown>> };
        if (dead) return;
        setFound(
          (data?.agents ?? [])
            .filter((a) => a?.id !== undefined && a.id !== null)
            .map((a) => {
              const handle = typeof a.username === 'string' ? a.username.replace(/^@/, '') : undefined;
              const name =
                (typeof a.displayName === 'string' && a.displayName) ||
                handle ||
                `Agent ${String(a.id).slice(0, 6)}`;
              const description =
                (typeof a.description === 'string' && a.description) ||
                (typeof a.bio === 'string' && a.bio) ||
                undefined;
              return { id: String(a.id), name, handle, owner: readOwner(a), trust: readTrust(a), description };
            }),
        );
      } catch {
        if (!dead) setFound([]);
      } finally {
        if (!dead) setSearching(false);
      }
    }, 250);
    return () => {
      dead = true;
      window.clearTimeout(timer);
    };
  }, [req, breadth, query]);

  if (!req) return null;

  const finish = (sel: AgentGrantSelection | null) => {
    req.resolve(sel);
    setReq(null);
  };
  const save = () => {
    if (breadth === 'find') {
      // A public agent is let in one at a time, by name, never as a breadth:
      // the ref carries `scope: ['public']` so the app can tell it from the
      // account's own and default it to suggesting rather than writing.
      const refs = [...picked.values()].map(
        (a): ResourceRef => ({ kind: 'agent', id: a.id, name: a.name, ...(a.handle ? { handle: a.handle } : {}), scope: ['public'] }),
      );
      if (!refs.length) return finish(null);
      return finish({ breadth, refs });
    }
    if (breadth === 'specific') {
      const refs = (roster ?? [])
        .filter((a) => checked.has(a.id))
        .map((a): ResourceRef => ({ kind: 'agent', id: a.id, name: a.name }));
      if (!refs.length) return finish(null);
      return finish({ breadth, refs });
    }
    finish({ breadth, refs: [{ kind: 'agent', id: '*', scope: [breadth] }] });
  };
  const toggle = (id: string) => {
    setChecked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else {
        if (!req.multi) next.clear();
        next.add(id);
      }
      return next;
    });
  };
  const togglePicked = (row: AgentRow) => {
    setPicked((cur) => {
      const next = new Map(cur);
      if (next.has(row.id)) next.delete(row.id);
      else {
        if (!req.multi) next.clear();
        next.set(row.id, row);
      }
      return next;
    });
  };
  const canSave = breadth === 'find' ? picked.size > 0 : !(breadth === 'specific' && checked.size === 0);

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel={t('widgetAgent.connectAgents')}
      // The panel's place comes from the classes below (a bottom sheet on a
      // phone, a card near the top on a desktop). The alignment was inline
      // too, and an inline style beats `md:items-start`, so on a desktop the
      // dialog sat at the bottom of the page (components/sharing/
      // share-dialog.tsx has the finding, 2026-10-02).
      style={{ position: 'fixed', inset: 0, zIndex: 240, display: 'flex', justifyContent: 'center' }}
      className="items-end bg-black/40 backdrop-blur-sm md:items-start md:py-[14vh]"
    >
      <div aria-hidden className="absolute inset-0" onClick={() => finish(null)} />
      <div
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'glass-panel relative flex w-full flex-col overflow-hidden shadow-2xl',
          'max-h-[90vh] rounded-t-2xl',
          'md:max-h-[70vh] md:w-[min(420px,96vw)] md:rounded-2xl',
        )}
      >
        <div className="shrink-0 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t('widgetAgent.connectAgents')}</div>
          <div className="truncate text-sm font-semibold tracking-tight">{req.widgetTitle} wants to talk to your agents</div>
          {req.reason && <div className="mt-0.5 truncate text-xs text-muted-foreground">{req.reason}</div>}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {BREADTHS.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setBreadth(b.id)}
              className={cn(
                'mb-1.5 flex w-full items-start gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors',
                breadth === b.id
                  ? 'border-primary/50 bg-primary/10'
                  : 'border-transparent hover:bg-muted/50',
              )}
            >
              <span
                className={cn(
                  'mt-1 h-3 w-3 shrink-0 rounded-full border',
                  breadth === b.id ? 'border-primary bg-primary' : 'border-muted-foreground/50',
                )}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{b.label}</span>
                <span className="block text-xs text-muted-foreground">{b.hint}</span>
              </span>
            </button>
          ))}

          {breadth === 'find' && (
            <div className="mt-1">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or what it does"
                aria-label="Search public agents"
                autoFocus
                className="w-full rounded-lg border border-muted-foreground/20 bg-background px-3 py-1.5 text-sm outline-none focus:border-primary/60"
              />
              <div className="mt-1.5 rounded-xl border border-muted-foreground/15 p-1.5">
                {query.trim().length < 2 && (
                  <div className="px-2 py-3 text-xs text-muted-foreground">Type at least two letters to search.</div>
                )}
                {query.trim().length >= 2 && searching && found === null && (
                  <div className="px-2 py-3 text-xs text-muted-foreground">Searching…</div>
                )}
                {query.trim().length >= 2 && found !== null && found.length === 0 && !searching && (
                  <div className="px-2 py-3 text-xs text-muted-foreground">No public agent matches that.</div>
                )}
                {(found ?? []).map((a) => (
                  <label
                    key={a.id}
                    className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted/50"
                  >
                    <input
                      type="checkbox"
                      checked={picked.has(a.id)}
                      onChange={() => togglePicked(a)}
                      className="mt-1 h-3.5 w-3.5 accent-current"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium">{a.name}</span>
                        {trustWord(a.trust) && (
                          <span className="shrink-0 rounded-full border border-muted-foreground/25 px-1.5 py-px text-[10px] uppercase tracking-wide text-muted-foreground">
                            {trustWord(a.trust)}
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {a.handle ? `@${a.handle}` : ''}
                        {a.owner ? ` · by @${a.owner}` : ''}
                      </span>
                      {a.description && (
                        <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{a.description}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
              <div className="mt-2 rounded-xl bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                An agent you let in can read what this app shows it, including the project&apos;s notes and comments.
                It only suggests changes unless you give it more inside the app, and each piece of work it is handed
                is capped by the usage you allow.
              </div>
            </div>
          )}
          {breadth === 'specific' && (
            <div className="mt-1 rounded-xl border border-muted-foreground/15 p-1.5">
              {roster === null && <div className="px-2 py-3 text-xs text-muted-foreground">{t('widgetAgent.loadingAgents')}</div>}
              {roster !== null && roster.length === 0 && (
                <div className="px-2 py-3 text-xs text-muted-foreground">{t('widgetAgent.noAgentsOnAccount')}</div>
              )}
              {(roster ?? []).map((a) => (
                <label
                  key={a.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={checked.has(a.id)}
                    onChange={() => toggle(a.id)}
                    className="h-3.5 w-3.5 accent-current"
                  />
                  <span className="truncate text-sm">{a.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 px-4 py-3 shadow-[inset_0_1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_1px_0_rgba(0,0,0,0.35)]">
          <button
            type="button"
            onClick={() => finish(null)}
            className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!canSave}
            className="rounded-lg bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-40"
          >
            {breadth === 'find' ? 'Let it in' : 'Connect'}
          </button>
        </div>
      </div>
    </PopoverPortal>
  );
}
